import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Clock } from "lucide-react";
import { nextMemoriaFase, type MemoriaFase } from "@juegossc/shared";
import { useAppSession } from "../hooks/useAppSession";
import { useAppState } from "../hooks/useAppState";
import { useBackgroundStyle } from "../hooks/useBackgroundStyle";
import {
  appMemoriaProductos,
  appMemoriaStart,
  appMemoriaWin,
  type MemoriaProductoElegible,
  type MemoriaStartResponse,
} from "../lib/api";
import { Modal } from "../components/ui/Modal";
import { PremioModal } from "../components/ui/PremioModal";
import { ModoPruebaBanner } from "../components/ui/Banner";
import { FitScreen } from "../components/landing/FitScreen";
import { MemoriaBoard } from "../components/memoria/MemoriaBoard";
import { playEvent } from "../lib/audio";

// Juego MEMORIA. Server-authoritative respecto al premio:
//   1. Cliente elige dificultad.
//   2. Server devuelve premios elegibles del nivel.
//   3. Cliente toca un premio.
//   4. /memoria/start: server crea session_id + devuelve tiempo + 6 imágenes.
//   5. Cliente memoriza (todas reveladas N segundos), después juega.
//   6. Si gana en tiempo: /memoria/win → server entrega el premio.
//   7. Si pierde: modal "Casi" sin nada.
//
// Sin reintento — la mecánica de memoria no lo soporta (es skill, no
// suerte).

type Nivel = "facil" | "medio" | "dificil";

// Fases del UI. Las 3 de "fin de partida" relevantes para el FSM están
// en @juegossc/shared como MemoriaFase. Acá extendemos con las fases
// de setup (elegir-nivel, elegir-premio, memorizar) que NO tienen
// races concurrentes.
type Fase =
  | "elegir-nivel"
  | "elegir-premio"
  | "memorizar"
  | MemoriaFase;

type Casilla = {
  id: number; // 0..11, posición en el grid
  imagen_idx: number; // 0..5, índice de la imagen (cada una aparece 2 veces)
  estado: "oculta" | "revelada" | "matcheada";
};

const NIVEL_LABELS: Record<Nivel, string> = {
  facil: "Fácil",
  medio: "Medio",
  dificil: "Difícil",
};

// Fisher–Yates shuffle (mutable). Determinístico no — cada partida es
// distinta. Para producción podríamos seedear con session_id pero
// para este caso aleatoreo del browser es suficiente.
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generarGrid(): Casilla[] {
  // 6 imágenes × 2 = 12 casillas. Mezclar las posiciones.
  const pares = [0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5];
  const mezclado = shuffle(pares);
  return mezclado.map((imagen_idx, id) => ({
    id,
    imagen_idx,
    estado: "oculta",
  }));
}

export default function Memoria() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSession();
  const { state, loading, error } = useAppState(isAuthenticated);
  // Hook ANTES de los early returns (regla §10).
  const bgStyle = useBackgroundStyle(state?.global);

  const [fase, setFase] = useState<Fase>("elegir-nivel");
  const [nivel, setNivel] = useState<Nivel | null>(null);
  const [premios, setPremios] = useState<MemoriaProductoElegible[]>([]);
  const [premioElegido, setPremioElegido] =
    useState<MemoriaProductoElegible | null>(null);
  const [session, setSession] = useState<MemoriaStartResponse | null>(null);
  const [grid, setGrid] = useState<Casilla[]>([]);
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Casilla "primera flipped" (esperando segunda comparación).
  const [primeraFlip, setPrimeraFlip] = useState<number | null>(null);
  // Par revelado que NO matcheó. Se mantiene visible (sin lock) hasta
  // que el jugador toque otra casilla oculta — en ese click, las 2 se
  // ocultan y se revela la nueva. Esto es para que jugadores rápidos
  // no tengan que esperar el delay de auto-ocultado.
  const [paresFalidos, setParesFalidos] = useState<[number, number] | null>(
    null,
  );
  const [showWinModal, setShowWinModal] = useState(false);
  // Modales de fin de partida con respiro visual antes de abrirse:
  //   - perdiste: overlay rojo 2s → modal "Casi!"
  //   - ganaste: parpadeo de las 12 casillas matcheadas 3 veces (mismo
  //     patrón que la ruleta) → modal del premio.
  // Sin esto, el modal saltaba INMEDIATO al detectar fin de partida y
  // el cliente no entendía visualmente qué pasó (especialmente si
  // gano/perdió justo en el último segundo).
  const [showLostModal, setShowLostModal] = useState(false);
  const [winFlashOn, setWinFlashOn] = useState(false);
  // Evita doble call a /memoria/win si el effect de detección de
  // victoria se dispara más de una vez en el mismo grid (puede pasar
  // con re-renders rápidos o StrictMode). El backend igual lo blockea
  // con `consumed=1`, pero acá ahorramos el round-trip y evitamos un
  // error visible al user cuando la 2da call falla.
  const winCalledRef = useRef(false);
  // Espejo síncrono de `fase` para chequear desde dentro de callbacks
  // viejos (el tick del setInterval del timer puede correr ENTRE
  // setFase('ganaste') y el cleanup del effect — sin esta ref el tick
  // ve la fase del closure y sigue decrementando el countdown un
  // segundo de más con la partida ya terminada).
  const faseRef = useRef<Fase>("elegir-nivel");
  useEffect(() => {
    faseRef.current = fase;
  }, [fase]);
  // Guard de unmount: si el cliente navega 'Atrás' mientras un fetch
  // está en vuelo (appMemoriaStart hasta 10s, appMemoriaWin hasta 10s),
  // los setState del .catch/.then se hacen sobre componente desmontado.
  // React 18 silencia el warning pero el leak es real.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) navigate("/setup", { replace: true });
  }, [isAuthenticated, navigate]);

  // Cargar productos del nivel elegido. Si vacío, mostrar mensaje.
  useEffect(() => {
    if (fase !== "elegir-premio" || !nivel) return;
    appMemoriaProductos(nivel)
      .then(setPremios)
      .catch((e) =>
        setErrorMsg(e instanceof Error ? e.message : String(e)),
      );
  }, [fase, nivel]);

  // Timer countdown durante la fase 'jugando'. Cuando llega a 0,
  // pasa a 'perdiste'. Si el cliente reveló todos los pares antes,
  // efecto separado pasa a 'ganaste'.
  //
  // GUARD CRITICO: usar setFase con functional update para revisar la
  // fase ACTUAL antes de pisar a "perdiste". Si el efecto de match-all
  // ya disparó setFase("ganaste") en este mismo tick, no queremos
  // sobrescribirlo. Sin este guard, un click ganador justo cuando
  // tiempo=1s podía terminar mostrando "perdiste".
  useEffect(() => {
    if (fase !== "jugando") return;
    const t = window.setInterval(() => {
      // Guard temprano: si la fase ya cambió (otro effect disparó
      // ganaste/perdiste) y el cleanup todavía no corrió, parar acá
      // mismo para no mostrar un tick fantasma del header "Tiempo: X"
      // mientras los modales/flash visual ya están ejecutándose.
      if (faseRef.current !== "jugando") {
        window.clearInterval(t);
        return;
      }
      setTiempoRestante((s) => {
        if (s <= 1) {
          window.clearInterval(t);
          // FSM puro (nextMemoriaFase) garantiza que si la partida ya
          // terminó por victoria (race con el effect de detección),
          // NO pisamos a 'perdiste'.
          setFase((current) =>
            current === "jugando" || current === "ganaste" || current === "perdiste"
              ? nextMemoriaFase(current as MemoriaFase, "timer-zero")
              : current,
          );
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [fase]);

  // Detección de victoria: todas las casillas matched.
  //
  // ORDEN CRÍTICO — bug que pagué caro: si esperamos el await
  // appMemoriaWin ANTES de setFase("ganaste"), abrimos una ventana de
  // race condition con el timer del countdown. Escenario real reportado:
  //   T=29.5s: cliente matchea último par → dispara appMemoriaWin (async).
  //   T=30s: timer dispara, lee fase==='jugando' (todavía, porque la
  //          respuesta no llegó) → setFase("perdiste") → modal perdiste.
  //   T=30.1s: respuesta llega → setFase("ganaste") → modal ganaste
  //          ENCIMA → el cliente ve los DOS modales.
  //
  // Fix: setFase("ganaste") + setShowWinModal(true) INMEDIATO, ANTES
  // del await. El timer chequea current==='jugando' y como ya pasó a
  // ganaste, no pisa. El /memoria/win sigue corriendo en background
  // como confirmación al server — el premio queda registrado allá.
  // Server sigue siendo authoritative para la entrega del premio
  // (§8); solo cambia que la UI no espera el round-trip para decidir.
  useEffect(() => {
    if (fase !== "jugando") return;
    if (grid.length === 0) return;
    if (!grid.every((c) => c.estado === "matcheada")) return;
    if (!session) return;
    if (winCalledRef.current) return;
    winCalledRef.current = true;
    // FSM puro garantiza simetría con el handler del timer: si el
    // timer ya disparó setFase("perdiste") (por un milisegundo, por
    // un closure stale, por orden de batch de React, etc.), NO lo
    // pisamos — preservamos el primer resultado.
    // El parpadeo de las casillas y la apertura del modal lo maneja un
    // useEffect dedicado que escucha fase === "ganaste" (ver abajo).
    setFase((current) =>
      current === "jugando" || current === "ganaste" || current === "perdiste"
        ? nextMemoriaFase(current as MemoriaFase, "all-matched")
        : current,
    );
    // Confirmación al server en background — ES la que REGISTRA la jugada
    // ganada y descuenta stock (POST /memoria/win). Tiene que correr hasta
    // el final.
    //
    // BUG prod (arreglado): antes esto usaba un AbortController con
    // `return () => ctrl.abort()`. Pero `fase` está en las deps y este mismo
    // effect la cambia a 'ganaste' -> el cleanup corría al instante y ABORTABA
    // la confirmación a los pocos ms, ANTES de que el server la procesara.
    // Doble daño: (1) la jugada no se registraba, y (2) apiCall traducía el
    // abort a "El servidor tardó demasiado" -> banner rojo de error JUSTO al
    // ganar, aunque la victoria fuera correcta. Fix: NO abortar. El fetch corre
    // hasta completar (registra el premio incluso si el cliente navega antes);
    // mountedRef evita el setState fantasma tras unmount.
    appMemoriaWin(session.session_id).catch((e) => {
      if (!mountedRef.current) return;
      // El cliente YA vio su victoria (fase 'ganaste' optimista). Si la
      // confirmación falla de verdad (server caído / timeout real), NO lo
      // alarmamos con un banner rojo sobre la pantalla de ganador — no es algo
      // que el jugador pueda resolver. Queda en consola para diagnóstico.
      console.error("memoria/win: confirmación falló:", e);
    });
  }, [grid, fase, session]);

  // Respiro visual perdiste: 2s de overlay rojo antes de abrir el
  // modal "Casi!". Le da al cliente tiempo de procesar visualmente
  // que el tiempo se acabó. Sin esto, el modal saltaba al toque y se
  // sentía abrupto — el cliente no entendía qué pasó.
  useEffect(() => {
    if (fase !== "perdiste") return;
    // Sonido de timeout — refactor sonidos 2026-06-02. Cae al sintético
    // 'lose' si no hay archivo cargado en el slot `${style}-memoria-lose`.
    playEvent(
      "memoria",
      "lose",
      state?.settings.sound_style_slug ?? "classic",
    );
    const t = window.setTimeout(() => setShowLostModal(true), 2000);
    return () => window.clearTimeout(t);
    // CRITICO: depender del SLUG (string), no del objeto `state`. useAppState
    // hace polling cada 30s (POLL_INTERVAL_MS) y entrega un `state` con NUEVA
    // referencia en cada ciclo; con `state` en deps este effect se re-ejecutaba
    // en cada poll mientras fase==='perdiste' y RE-DISPARABA el sonido de
    // derrota cada ~30s sin que el cliente tocara nada (el bug reportado). El
    // slug es estable entre polls -> ahora suena exactamente una vez.
  }, [fase, state?.settings.sound_style_slug]);

  // Respiro visual ganaste: parpadeo 3 veces de las casillas matcheadas
  // antes de abrir el modal del premio. Mismo timing que la ruleta
  // (180ms ON / 130ms OFF × 3 = 930ms total) + 150ms de respiro
  // inicial — el cliente ve las casillas "celebrar" antes del modal.
  //
  // Pattern: toggle binario winFlashOn → CSS condicional en cada
  // casilla matcheada. Cuando termina el ciclo, setShowWinModal(true).
  useEffect(() => {
    if (fase !== "ganaste") return;
    const ON_MS = 180;
    const OFF_MS = 130;
    const BLINK_COUNT = 3;
    let i = 0;
    let on = false;
    const timeouts: number[] = [];
    function tick() {
      if (i >= BLINK_COUNT * 2) {
        // Fin del parpadeo. Estado limpio + abrir modal.
        setWinFlashOn(false);
        setShowWinModal(true);
        return;
      }
      on = !on;
      setWinFlashOn(on);
      i++;
      timeouts.push(window.setTimeout(tick, on ? ON_MS : OFF_MS));
    }
    timeouts.push(window.setTimeout(tick, 150));
    return () => {
      timeouts.forEach((t) => window.clearTimeout(t));
    };
  }, [fase]);

  // Cuenta atrás de memorizar → pasa a jugando.
  useEffect(() => {
    if (fase !== "memorizar" || !session) return;
    const t = window.setTimeout(() => {
      // Ocultar todas las casillas y arrancar el juego.
      setGrid((g) => g.map((c) => ({ ...c, estado: "oculta" })));
      setTiempoRestante(session.tiempo_segundos);
      setFase("jugando");
    }, session.seg_memorizar * 1000);
    return () => window.clearTimeout(t);
  }, [fase, session]);

  // Las 6 imágenes para mapear imagen_idx → url en runtime.
  //
  // Por qué ACÁ arriba y no debajo de los early returns: regla §10
  // de SYSTEM.md — TODOS los hooks deben llamarse en el mismo orden
  // en cada render. Si lo dejamos abajo, en el primer render con
  // loading=true este useMemo NO se llama, pero en el siguiente
  // render con state cargado sí → React tira "Rendered more hooks
  // than during the previous render" y crashea el componente.
  // state puede ser null acá (mientras carga), por eso el guard.
  const imagenesUrl = useMemo<Array<string | null>>(
    () =>
      state
        ? [
            state.global.memoria_imagen_1_url,
            state.global.memoria_imagen_2_url,
            state.global.memoria_imagen_3_url,
            state.global.memoria_imagen_4_url,
            state.global.memoria_imagen_5_url,
            state.global.memoria_imagen_6_url,
          ]
        : [null, null, null, null, null, null],
    [state],
  );

  function elegirNivel(n: Nivel) {
    setNivel(n);
    setErrorMsg(null);
    setFase("elegir-premio");
  }

  async function elegirPremio(p: MemoriaProductoElegible) {
    if (!nivel) return;
    setPremioElegido(p);
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const s = await appMemoriaStart(p.id, nivel);
      // Si el cliente navegó "Atrás" durante el await (hasta 10s),
      // no aplicamos los setState — son setState fantasma y dejan
      // basura si el cliente vuelve a entrar al juego.
      if (!mountedRef.current) return;
      setSession(s);
      // Generar grid + mostrar todas reveladas para memorizar.
      const newGrid = generarGrid().map((c) => ({
        ...c,
        estado: "revelada" as const,
      }));
      setGrid(newGrid);
      setFase("memorizar");
    } catch (e) {
      if (!mountedRef.current) return;
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  function handleClickCasilla(idx: number) {
    if (fase !== "jugando") return;
    const casilla = grid[idx];
    if (casilla.estado !== "oculta") return; // ya revelada o matcheada

    // Si hay un par fallido del intento anterior visible, lo ocultamos
    // EN ESTE MISMO click antes de revelar la nueva. Así no hay lock ni
    // delay — el jugador puede tirar siguiente casilla inmediatamente.
    // Construimos una sola actualización de grid para evitar flicker.
    let base = grid;
    if (paresFalidos !== null) {
      const [a, b] = paresFalidos;
      base = grid.map((c, i) =>
        i === a || i === b ? { ...c, estado: "oculta" as const } : c,
      );
      setParesFalidos(null);
    }
    // newGrid = base con la nueva casilla revelada.
    const newGrid = [...base];
    newGrid[idx] = { ...newGrid[idx], estado: "revelada" };

    const style = state?.settings.sound_style_slug ?? "classic";
    // flip al revelar la casilla (siempre, sea primera o segunda del par).
    playEvent("memoria", "flip", style);

    if (primeraFlip === null) {
      // Es la primera de un par; esperar el segundo click.
      setGrid(newGrid);
      setPrimeraFlip(idx);
      return;
    }

    // Es la segunda. Comparar contra la primera (que sigue revelada en
    // base — no la afecta el reset del par fallido porque sigue siendo
    // primeraFlip pendiente).
    const primera = base[primeraFlip];
    if (primera.imagen_idx === casilla.imagen_idx) {
      // Match: marcamos ambas como 'matcheada' INMEDIATAMENTE (sin
      // setTimeout). Antes había un delay de 250ms estético, pero
      // generaba dos bugs:
      //   1) Si el usuario hacía click rápido durante esos 250ms, el
      //      state primeraFlip seguía apuntando al valor viejo →
      //      podía pisar el grid en estados inconsistentes.
      //   2) Si era la última pareja y el timer estaba en 1s, el
      //      setTimeout podía disparar DESPUÉS de que el timer pasó
      //      a "perdiste" → mostraba derrota en una victoria real.
      // Sin delay todo se renderea en el mismo commit de React y el
      // efecto de detección de victoria ve el grid completo en el
      // siguiente render, antes de que el timer pueda intervenir.
      const matchedGrid = newGrid.map((c, i) =>
        i === primeraFlip || i === idx
          ? { ...c, estado: "matcheada" as const }
          : c,
      );
      setGrid(matchedGrid);
      setPrimeraFlip(null);
      playEvent("memoria", "match", style);
    } else {
      // No match: las 2 quedan reveladas como "par fallido" hasta el
      // próximo click en una casilla oculta (entonces se ocultan).
      // Sin lock, sin delay automático.
      setGrid(newGrid);
      setParesFalidos([primeraFlip, idx]);
      setPrimeraFlip(null);
      playEvent("memoria", "mismatch", style);
    }
  }

  function reset() {
    setFase("elegir-nivel");
    setNivel(null);
    setPremios([]);
    setPremioElegido(null);
    setSession(null);
    setGrid([]);
    setTiempoRestante(0);
    setPrimeraFlip(null);
    setParesFalidos(null);
    setErrorMsg(null);
    setShowWinModal(false);
    setShowLostModal(false);
    setWinFlashOn(false);
    // Habilitar la detección de victoria para el próximo juego (el ref
    // bloquea durante la partida actual para evitar dobles calls).
    winCalledRef.current = false;
  }

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </main>
    );
  }

  if (error || !state) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-red-400">No se pudo cargar.</p>
        <button
          onClick={() => navigate("/")}
          className="rounded-lg bg-white/10 px-4 py-2"
        >
          Volver
        </button>
      </main>
    );
  }

  if (!state.settings.memoria_habilitada) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p>Memoria no habilitada en esta sucursal.</p>
        <button
          onClick={() => navigate("/")}
          className="rounded-lg bg-white/10 px-4 py-2"
        >
          Volver
        </button>
      </main>
    );
  }

  // (imagenesUrl ya se calculó arriba antes de los early returns —
  // regla §10. Acá solo derivamos el flag visual.)
  const faltanImagenes = imagenesUrl.some((u) => u === null);

  // Migration 037: posición vertical del cuerpo del juego (deslizador).
  const topGrow = Math.max(0, Math.min(100, state.global.juego_vertical_pos_pct));
  const bottomGrow = 100 - topGrow;

  return (
    <>
      {state.settings.modo_prueba && (
        <div className="fixed left-0 top-0 z-[60] w-full">
          <ModoPruebaBanner />
        </div>
      )}

      <FitScreen bgStyle={bgStyle}>
        <div className="app-mode flex h-full w-full flex-col">
          <header className="flex items-center justify-between gap-4 p-4">
            <button
              onClick={() => navigate("/")}
              className="flex shrink-0 items-center gap-2 rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
              Atrás
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-center">
              {state.campana?.imagen_url ? (
                <img
                  src={state.campana.imagen_url}
                  alt={state.campana.nombre_display}
                  style={{
                    height: `${state.global.campana_juego_height_px}px`,
                    marginTop: `${state.global.campana_juego_top_margin_px}px`,
                  }}
                  className="w-auto max-w-full shrink-0 object-contain drop-shadow-lg"
                />
              ) : (
                <h1 className="font-heading text-xl font-bold opacity-80">
                  MEMORIA
                </h1>
              )}
            </div>
            <div className="w-20 shrink-0" />
          </header>

          <section className="flex flex-1 flex-col items-center p-4">
            <div aria-hidden style={{ flex: `${topGrow} 1 0` }} />
            <div className="flex shrink-0 flex-col items-center gap-6">
        {faltanImagenes && (
          <div className="rounded-lg bg-amber-500/15 px-4 py-3 text-sm text-amber-300">
            Falta cargar las 6 imágenes del tablero. Pedile al encargado
            del sistema que las suba.
          </div>
        )}

        {errorMsg && (
          <div className="rounded-lg bg-red-500/15 px-4 py-3 text-sm text-red-300">
            {errorMsg}
          </div>
        )}

        {/* Fase 1: elegir nivel */}
        {!faltanImagenes && fase === "elegir-nivel" && (
          <div className="flex flex-col items-center gap-4">
            <h2 className="font-heading text-3xl font-bold">
              Elegí dificultad
            </h2>
            <p className="font-body opacity-70">
              Cuanto más difícil, mejor premio, menos tiempo.
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {(["facil", "medio", "dificil"] as Nivel[]).map((n) => {
                const seg =
                  n === "facil"
                    ? state.settings.memoria_tiempo_facil
                    : n === "medio"
                    ? state.settings.memoria_tiempo_medio
                    : state.settings.memoria_tiempo_dificil;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => elegirNivel(n)}
                    className="font-display flex w-48 flex-col items-center gap-2 rounded-2xl border-2 border-white/10 bg-white/5 px-6 py-8 transition hover:scale-[1.03] hover:border-[#a78bfa] hover:bg-[#a78bfa]/10"
                  >
                    <span className="text-3xl font-bold">{NIVEL_LABELS[n]}</span>
                    <span className="flex items-center gap-1 text-sm opacity-70">
                      <Clock className="h-4 w-4" /> {seg}s
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fase 2: elegir premio del nivel */}
        {fase === "elegir-premio" && nivel && (
          <div className="flex flex-col items-center gap-4">
            <h2 className="font-heading text-3xl font-bold">
              Elegí tu premio ({NIVEL_LABELS[nivel]})
            </h2>
            <p className="font-body opacity-70">
              Si encontrás todos los pares en {/* tiempo */}
              {nivel === "facil"
                ? state.settings.memoria_tiempo_facil
                : nivel === "medio"
                ? state.settings.memoria_tiempo_medio
                : state.settings.memoria_tiempo_dificil}
              s, te lo llevás.
            </p>
            {premios.length === 0 ? (
              <div className="rounded-lg bg-white/5 px-6 py-8 text-center opacity-70">
                No hay premios disponibles en {NIVEL_LABELS[nivel]}{" "}
                actualmente.
                <button
                  onClick={reset}
                  className="mt-4 block rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
                >
                  Elegir otra dificultad
                </button>
              </div>
            ) : (
              <div className="grid max-w-3xl grid-cols-2 gap-3 md:grid-cols-3">
                {premios.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => elegirPremio(p)}
                    disabled={submitting}
                    // disabled:cursor-wait — feedback discreto durante el
                    // fetch a /memoria/start (cliente ve el cursor cambiar
                    // sin spinner visual fuerte). Equivalente a "opción B"
                    // para memoria: en el caso típico (~80-200ms) la fase
                    // pasa a 'memorizar' antes de que se note nada; si
                    // tarda más (cold start), el cursor wait da pista de
                    // que la app está procesando.
                    className="font-display rounded-xl border-2 border-white/10 bg-white/5 px-4 py-6 text-center transition hover:scale-[1.03] hover:border-[#a78bfa] hover:bg-[#a78bfa]/10 disabled:cursor-wait disabled:opacity-50"
                    style={{ minWidth: 120 }}
                  >
                    <div className="text-lg font-bold leading-tight">
                      {p.nombre}
                    </div>
                    {p.stock !== null && (
                      <div className="mt-1 text-xs opacity-50">
                        Stock: {p.stock}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={reset}
              className="mt-2 rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
            >
              ← Cambiar dificultad
            </button>
          </div>
        )}

        {/* Fase 3-5: tablero (memorizar / jugando / ganaste / perdiste).
            Markup COMPARTIDO con el preview del admin (MemoriaBoard) -> el
            preview coincide con la pantalla real. */}
        {(fase === "memorizar" ||
          fase === "jugando" ||
          fase === "ganaste" ||
          fase === "perdiste") &&
          session && (
            <MemoriaBoard
              tiempoDisplay={
                fase === "memorizar" ? session.seg_memorizar : tiempoRestante
              }
              premioNombre={premioElegido?.nombre}
              mostrarMemorizar={fase === "memorizar"}
              grid={grid}
              imagenesUrl={imagenesUrl}
              winFlashOn={winFlashOn}
              clickeable={fase === "jugando"}
              onCardPointerDown={handleClickCasilla}
              cardSize={state.global.memoria_carta_px}
            />
          )}

            </div>
            <div aria-hidden style={{ flex: `${bottomGrow} 1 0` }} />
          </section>
        </div>
      </FitScreen>

      {/* Modales/overlays a pantalla completa, AFUERA del lienzo escalado: un
          fixed dentro del transform de FitScreen se ancla al lienzo en vez del
          viewport (modal chico/descentrado). */}
      {fase === "ganaste" && premioElegido && (
        <PremioModal
          open={showWinModal}
          premioNombre={premioElegido.nombre}
          isConsolation={false}
          isReintento={false}
          soundStyle={state.settings.sound_style_slug}
          gameType="memoria"
          onNext={reset}
          onMainMenu={() => navigate("/")}
        />
      )}
      {/* Overlay rojo de "perdiste" antes de abrir el modal (2s de fade). */}
      {fase === "perdiste" && !showLostModal && (
        <div
          className="pointer-events-none fixed inset-0 z-40 animate-[memoria-lost-fade_2s_ease-out_forwards] bg-red-600"
          aria-hidden="true"
        />
      )}
      {fase === "perdiste" && (
        <Modal open={showLostModal}>
          <div className="flex flex-col items-center gap-4 p-2 text-center">
            {/* Mismo lenguaje visual que el PremioModal de consolation:
                trebol desaturado/atenuado = "no fue esta vez" sin agresividad. */}
            <div className="text-6xl opacity-80 saturate-50">🍀</div>
            <h2 className="font-heading text-3xl font-bold">¡Casi!</h2>
            <p className="font-body opacity-70">
              El tiempo se acabó. Probá otra vez con menos dificultad o volvé al
              menú.
            </p>
            <div className="mt-2 flex w-full flex-col gap-2">
              <button
                type="button"
                onClick={reset}
                className="font-display rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold"
              >
                Volver a jugar
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="rounded-lg border-2 border-white/20 bg-white/5 py-3 font-bold hover:bg-white/10"
              >
                Menú principal
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
